import { Composer, Thread } from "@liveblocks/react-ui";
import "@/features/collaboration/styles/_comments.scss";
import { useAppSelector } from "@store";
import { getActiveEntityId, getCurrentPayloadId } from "@store/story/selectors";

import { Suspense } from "@/features/collaboration";
import { CollaborationEnabledProvider } from "@/features/collaboration/providers";
import { trackContent } from "@/pages/editor/utils";
import type { ThreadData } from "@liveblocks/client";
import { useThreads } from "@liveblocks/react/suspense";
import { useEffect } from "react";
import { useSuggestionMutation } from "../../../hooks";
import { Error as ErrorComponent } from "./Error";
import { InviteUser } from "./InviteUser";
import { Skeleton } from "./Skeleton";

const CONTAINER_STYLE =
  "tw-relative tw-min-h-24 tw-w-[400px] tw-overflow-hidden tw-overflow-y-auto tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-shadow-comment-popover tw-max-h-[60vh] scrollbar tw-overflow-y-auto";

export const Comments = ({ id, close }: { id: string; close: () => void }) => {
  return (
    <CollaborationEnabledProvider>
      <Suspense
        fallback={<ErrorComponent />}
        loader={<Skeleton id={id} />}
        feature="Comment"
      >
        <Content close={close} />
      </Suspense>
    </CollaborationEnabledProvider>
  );
};

const Content = ({ close }: { close: () => void }) => {
  const { ref, search } = useSuggestionMutation();
  const activeId = useAppSelector(getActiveEntityId);
  const storyId = useAppSelector(getCurrentPayloadId);

  useEffect(() => {
    trackContent("click add comment");
  }, []);

  const { threads } = useThreads({
    query: {
      resolved: false,
      metadata: {
        entityId: activeId,
        storyId,
      },
    },
  });
  return (
    <div ref={ref} id="vyqdiRYC2qsIgAX">
      <div className="tw-space-y-2">
        {threads?.map((thread) => (
          <SingleThread thread={thread} close={close} key={thread.id} />
        ))}

        {threads?.length === 0 && (
          <div className={CONTAINER_STYLE}>
            <Composer
              autoFocus
              metadata={{
                entityId: activeId,
                storyId,
              }}
            />
          </div>
        )}
      </div>
      <InviteUser search={search} />
    </div>
  );
};

const SingleThread = ({
  thread,
  close,
}: {
  thread: ThreadData;
  close: () => void;
}) => {
  return (
    <div className={CONTAINER_STYLE}>
      <Thread
        showComposer
        thread={thread}
        onThreadDelete={close}
        onResolvedChange={(resolved) => {
          if (!resolved) return;
          close();
          trackContent("click resolve comment");
        }}
      />
    </div>
  );
};
